import type { ConversationThread, ThreadSpace, UserSettings } from '../../types';
import { DEFAULT_USER_SETTINGS } from '../../constants';
import { redactClaimWords, redactComposedTurn, redactTurns } from '../../components/Agents/redact';
import { writerPersisted } from '../slices/writerPersisted';

// ============================================================
// Conversation thread localStorage persistence
//
// Threads/spaces are namespaced by the signed-in Clerk user so two accounts
// sharing a browser never see each other's question history. Without Clerk
// (local dev) the legacy un-namespaced keys are used, exactly as before.
// ============================================================

let _namespace: string | null = null;
const _namespaceListeners: Array<() => void> = [];

/** Set the persistence namespace (Clerk user id). Notifies subscribers when it changes. */
export function setPersistenceNamespace(ns: string | null) {
  const next = ns || null;
  if (next === _namespace) return;
  _namespace = next;
  _namespaceListeners.forEach((cb) => { try { cb(); } catch {} });
}

/** Subscribe to namespace changes (used by the store to reload threads/spaces). */
export function onPersistenceNamespaceChange(cb: () => void) {
  _namespaceListeners.push(cb);
}

/** The namespace in force now — captured by a request so a late result can be parked where it belongs. */
export function getPersistenceNamespace(): string | null {
  return _namespace;
}

/** `ns` undefined means the namespace in force now; `null` is the no-namespace key. */
function nsKey(base: string, ns?: string | null): string {
  const use = ns === undefined ? _namespace : ns;
  return use ? `${base}::${use}` : base;
}

export function loadThreads(): ConversationThread[] {
  try {
    const raw = localStorage.getItem(nsKey('sde_threads'));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function persistThreads(threads: ConversationThread[]) {
  // a thread's turns may hold a walk with claims from the brain (#296): this device keeps them by reference only
  try { localStorage.setItem(nsKey('sde_threads'), JSON.stringify(redactClaimWords(threads))); } catch {}
}

/**
 * An agent chat's snapshot the server has not taken yet (#202), parked so a
 * reload before the next message does not lose a line the agent already
 * acted on. Keyed by namespace like the threads: a workspace switch never
 * replays another workspace's park.
 */

export interface ParkedAgentChat {
  threadId: string;
  kind: 'agent_chat' | 'group';
  title: string;
  source: { kind: string; id: string | null };
  turns: any[];
  at: string;
}

export function loadParkedAgentChats(ns?: string | null): ParkedAgentChat[] {
  try {
    const raw = localStorage.getItem(nsKey('sde_agent_chat_park', ns));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

export function persistParkedAgentChats(parked: ParkedAgentChat[], ns?: string | null) {
  try {
    if (parked.length === 0) localStorage.removeItem(nsKey('sde_agent_chat_park', ns));
    // a composed answer is parked by its references (#296): this device keeps no claim's words past the session
    // and (F1, #1806) no Writer words: a data answer as the server stores it (`writerPersisted`)
    else localStorage.setItem(nsKey('sde_agent_chat_park', ns), JSON.stringify(parked.map((p) => ({ ...p, turns: redactTurns(writerPersisted(p.turns)) }))));
  } catch {}
}

/**
 * Park a chat snapshot under a namespace — the one in force now, or the one
 * a request captured before an identity/org switch. Joined by turn id with
 * whatever is already parked for the thread: a newer turn replaces the one
 * it names, an unknown one is appended, nothing parked is lost.
 */
export function parkAgentChatSnapshot(entry: ParkedAgentChat, ns?: string | null) {
  const parked = loadParkedAgentChats(ns);
  const existing = parked.find((p) => p.threadId === entry.threadId);
  let turns = entry.turns;
  if (existing && Array.isArray(existing.turns)) {
    const merged = [...existing.turns];
    for (const t of entry.turns) {
      const i = merged.findIndex((m) => m?.id === t?.id);
      if (i >= 0) merged[i] = t; else merged.push(t);
    }
    turns = merged;
  }
  persistParkedAgentChats([{ ...entry, turns }, ...parked.filter((p) => p.threadId !== entry.threadId)], ns);
}

/**
 * A flush landed: the turns it carried are no longer owed. Only those leave
 * the park — by id, and only as the same turn (a parked newer version of an
 * id, say a `failed` mark that changed, stays). Turns a later snapshot
 * added, parked while this flush was in flight, stay until a write of
 * their own lands; an entry with nothing left is removed.
 */
export function confirmParkedAgentChatTurns(threadId: string, confirmed: any[], ns?: string | null) {
  const parked = loadParkedAgentChats(ns);
  const entry = parked.find((p) => p.threadId === threadId);
  if (!entry) return;
  // parked turns are kept by reference (#296), so a confirmed turn is matched by id and role, never by its words:
  // the same id, whether the park holds the words or the reference, is the same turn
  const sent = new Map<string, string>();
  for (const t of confirmed) if (t?.id != null) sent.set(String(t.id), JSON.stringify(redactComposedTurn(t)));
  const remaining = (Array.isArray(entry.turns) ? entry.turns : []).filter((t) => sent.get(String(t?.id)) !== JSON.stringify(redactComposedTurn(t)));
  const rest = parked.filter((p) => p.threadId !== threadId);
  persistParkedAgentChats(remaining.length ? [{ ...entry, turns: remaining }, ...rest] : rest, ns);
}

export function unparkAgentChatSnapshot(threadId: string, ns?: string | null) {
  const parked = loadParkedAgentChats(ns);
  if (parked.some((p) => p.threadId === threadId)) persistParkedAgentChats(parked.filter((p) => p.threadId !== threadId), ns);
}

export function loadSpaces(): ThreadSpace[] {
  try {
    const raw = localStorage.getItem(nsKey('sde_spaces'));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function persistSpaces(spaces: ThreadSpace[]) {
  try { localStorage.setItem(nsKey('sde_spaces'), JSON.stringify(spaces)); } catch {}
}

// ============================================================
// Pinned numbers (#183) — the Home pins queue, per signed-in account
// ============================================================

export function loadPins(): any[] {
  try {
    const raw = localStorage.getItem(nsKey('sde_pins'));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function persistPins(pins: any[]) {
  // a pinned quote is kept by its span, never its words (#296); Home re-reads it through the viewer's keys
  try { localStorage.setItem(nsKey('sde_pins'), JSON.stringify(redactClaimWords(pins))); } catch {}
}

// ============================================================
// Pinned perspectives localStorage persistence
// ============================================================

export const PINNED_STORAGE_KEY = 'dx-pinned-perspectives';

export function loadPinnedPerspectives(): string[] {
  try {
    const raw = localStorage.getItem(PINNED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ============================================================
// User settings localStorage persistence
// ============================================================

export const SETTINGS_STORAGE_KEY = 'dx-settings';

export function loadUserSettings(): UserSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? { ...DEFAULT_USER_SETTINGS, ...JSON.parse(raw) } : { ...DEFAULT_USER_SETTINGS };
  } catch {
    return { ...DEFAULT_USER_SETTINGS };
  }
}

export function saveUserSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}
